/* TEF Reward Popup — v2.1
   Visual update:
   - Upper-third placement
   - Pre-Tier-1 popup has no stars
   - Pre-Tier-1 message uses generic "a Mystery Gift"

   Based on stable v1.4 reward logic.
*/

(() => {
  const CONFIG = {
    tiers: [
      {
        threshold: 50,
        label: "Mystery Gift ✦✧✧",
        stars: "✦✧✧"
      },
      {
        threshold: 100,
        label: "Mystery Gift ✦✦✧",
        stars: "✦✦✧"
      },
      {
        threshold: 150,
        label: "Mystery Gift ✦✦✦",
        stars: "✦✦✦"
      }
    ],

    showFirstProgressMessage: true,
    autoHideMs: 6500,
    cartSettleMs: 80
  };

  let lastCartTotal = null;
  let lastTrigger = null;
  let hideTimer = null;
  let initialized = false;
  let cartCheckQueue = Promise.resolve();

  const popup = () =>
    document.getElementById(
      "tef-reward-popup"
    );

  const money = (amount) => {
    const currency =
      window.Shopify?.currency?.active ||
      document.documentElement
        .dataset
        .shopifyCurrency ||
      "USD";

    try {
      return new Intl.NumberFormat(
        undefined,
        {
          style: "currency",
          currency,
          maximumFractionDigits: 2
        }
      ).format(amount);
    } catch {
      return `$${amount.toFixed(2)}`;
    }
  };

  const getCart = async () => {
    const response =
      await window.fetch(
        "/cart.js",
        {
          credentials:
            "same-origin",
          cache: "no-store",
          headers: {
            Accept:
              "application/json"
          }
        }
      );

    if (!response.ok) {
      throw new Error(
        "Cart request failed"
      );
    }

    return response.json();
  };

  const isCartAddUrl = (url) => {
    try {
      const parsed =
        new URL(
          url,
          window.location.origin
        );

      return (
        parsed.origin ===
          window.location.origin &&
        /\/cart\/add(?:\.js)?$/i.test(
          parsed.pathname
        )
      );
    } catch {
      return /\/cart\/add(?:\.js)?(?:[?#]|$)/i.test(
        String(url)
      );
    }
  };

  const isCartChangeUrl = (url) => {
    try {
      const parsed =
        new URL(
          url,
          window.location.origin
        );

      return (
        parsed.origin ===
          window.location.origin &&
        /\/cart\/change(?:\.js)?$/i.test(
          parsed.pathname
        )
      );
    } catch {
      return /\/cart\/change(?:\.js)?(?:[?#]|$)/i.test(
        String(url)
      );
    }
  };

  const isCartAddRequest = (
    input,
    init = {}
  ) => {
    const url =
      typeof input === "string"
        ? input
        : input?.url || "";

    const method =
      (
        init.method ||
        (
          typeof input !==
            "string" &&
          input?.method
        ) ||
        "GET"
      ).toUpperCase();

    return (
      method === "POST" &&
      isCartAddUrl(url)
    );
  };

  const isCartChangeRequest = (
    input,
    init = {}
  ) => {
    const url =
      typeof input === "string"
        ? input
        : input?.url || "";

    const method =
      (
        init.method ||
        (
          typeof input !==
            "string" &&
          input?.method
        ) ||
        "GET"
      ).toUpperCase();

    return (
      method === "POST" &&
      isCartChangeUrl(url)
    );
  };

  const getTier = (total) => {
    let tier = -1;

    CONFIG.tiers.forEach(
      (item, index) => {
        if (
          total >=
          item.threshold
        ) {
          tier = index;
        }
      }
    );

    return tier;
  };

  /*
   * ---------------------------------------------------------
   * POPUP POSITION
   * ---------------------------------------------------------
   *
   * Upper-third placement:
   * - 10% from viewport top
   * - 12px from right edge
   */
  const positionNearTrigger = () => {
    const node = popup();

    if (!node) {
      return;
    }

    const width =
      Math.min(
        360,
        window.innerWidth - 24
      );

    node.style.left =
      "auto";

    node.style.right =
      "12px";

    node.style.bottom =
      "auto";

    node.style.width =
      `${width}px`;

    const top =
      Math.max(
        24,
        Math.round(
          window.innerHeight *
            0.10
        )
      );

    node.style.top =
      `${top}px`;
  };

  /*
   * ---------------------------------------------------------
   * SHOW POPUP
   * ---------------------------------------------------------
   */
  const showPopup = ({
    title,
    eyebrow,
    message,
    stars = "",
    celebration = true,
    tierIndex = null
  }) => {
    const node = popup();

    if (!node) {
      return;
    }

    clearTimeout(
      hideTimer
    );

    const eyebrowNode =
      node.querySelector(
        "[data-popup-eyebrow]"
      );

    const titleNode =
      node.querySelector(
        "[data-popup-title]"
      );

    const messageNode =
      node.querySelector(
        "[data-popup-message]"
      );

    const starsNode =
      node.querySelector(
        "[data-popup-stars]"
      );

    const iconWrap =
      node.querySelector(
        ".tef-reward-popup__icon-wrap"
      );

    if (eyebrowNode) {
      eyebrowNode.textContent =
        eyebrow;
    }

    if (titleNode) {
      titleNode.textContent =
        title;
    }

    if (messageNode) {
      messageNode.textContent =
        message;
    }

    /*
     * For the pre-Tier-1 message this is intentionally empty.
     *
     * The CSS can therefore hide the lower stars whenever
     * there is no star content.
     */
    if (starsNode) {
      if (
        celebration &&
        Number.isInteger(tierIndex) &&
        tierIndex === 2
      ) {
        starsNode.innerHTML =
          [...stars]
            .map(
              (star, index) =>
                `<span class="tef-orbit-star tef-orbit-star-${index + 1}" aria-hidden="true">${star}</span>`
            )
            .join("");
      } else {
        starsNode.textContent =
          stars;
      }
    }

    /*
     * IMPORTANT:
     *
     * celebration = false for the pre-Tier-1 popup.
     *
     * This removes the celebration treatment, including
     * the stars above the cactus, while leaving celebration
     * effects intact for actual tier unlocks.
     */
    node.classList.toggle(
      "is-celebration",
      celebration
    );

    /*
     * Restore the actual-tier state used by the CSS.
     * This hides the old middle "Mystery Gift ✦✧✧" title
     * and keeps the celebration stars in front.
     */
    node.classList.toggle(
      "is-tier-unlock",
      celebration
    );

    /*
     * Tier-specific visual state for the v2.0 celebration polish.
     * Tier 3 gets the special star-orbit treatment.
     */
    node.classList.remove(
      "tier-1",
      "tier-2",
      "tier-3"
    );

    if (
      celebration &&
      Number.isInteger(tierIndex) &&
      tierIndex >= 0 &&
      tierIndex <= 2
    ) {
      node.classList.add(
        `tier-${tierIndex + 1}`
      );
    }

    /*
     * Give the popup a state class when no stars are present.
     *
     * This lets the CSS hide the star row cleanly without
     * affecting the actual tier-unlock popups.
     */
    node.classList.toggle(
      "no-stars",
      !stars
    );

    /*
     * Lightweight tier confetti.
     *
     * Particles are created only for real tier unlocks,
     * burst once, then remove themselves. No library/canvas.
     */
    if (iconWrap) {
      const oldConfetti =
        iconWrap.querySelector(
          ".tef-reward-popup__confetti"
        );

      oldConfetti?.remove();

      if (
        celebration &&
        Number.isInteger(tierIndex) &&
        tierIndex >= 0 &&
        tierIndex <= 2
      ) {
        const confetti =
          document.createElement(
            "div"
          );

        confetti.className =
          "tef-reward-popup__confetti";

        const counts = [
          20,
          30,
          42
        ];

        const count =
          counts[tierIndex];

        const particles = [
          [-42, -20, -18, 5, 0],
          [38, -27, 24, 4, 35],
          [-52, 5, 32, 4, 70],
          [50, 9, -28, 5, 95],
          [-31, 30, 15, 4, 120],
          [29, 34, -20, 5, 145],
          [-64, -10, -8, 3, 165],
          [63, -2, 12, 4, 190],
          [-18, -42, 42, 4, 215],
          [17, -45, -35, 5, 235],
          [-72, 20, 18, 4, 255],
          [70, 23, -24, 3, 275],
          [-45, 42, -30, 5, 295],
          [46, 44, 22, 4, 315],
          [0, -55, 8, 4, 335],
          [0, 54, -12, 5, 355],
          [-82, -25, -15, 3, 375],
          [80, -18, 18, 4, 395],
          [-76, 39, 28, 4, 415],
          [76, 38, -28, 3, 435],
          [0, 68, 10, 4, 455],

          /* Extra fine particles for the dense experiment. */
          [ -24, -14, 28, 2, 65 ],
          [ 24, -16, -32, 2, 85 ],
          [ -36, -2, -20, 2, 110 ],
          [ 35, 2, 18, 2, 135 ],
          [ -25, 18, 40, 2, 155 ],
          [ 26, 19, -42, 2, 175 ],
          [ -45, -17, 8, 2, 205 ],
          [ 44, -12, -14, 2, 225 ],
          [ -12, -32, 22, 2, 245 ],
          [ 13, -34, -28, 2, 265 ],
          [ -57, 10, 35, 2, 285 ],
          [ 56, 13, -35, 2, 305 ],
          [ -35, 32, 12, 2, 325 ],
          [ 36, 31, -16, 2, 345 ],
          [ -8, -50, 30, 2, 365 ],
          [ 9, -49, -30, 2, 385 ],
          [ -62, -31, 6, 2, 405 ],
          [ 61, -27, -10, 2, 425 ],
          [ -58, 35, 24, 2, 445 ],
          [ 57, 34, -24, 2, 465 ],
          [ 0, 58, 0, 2, 485 ]
        ];

        for (
          let i = 0;
          i < count;
          i++
        ) {
          const [
            x,
            y,
            rotation,
            size,
            delay
          ] = particles[i];

          const particle =
            document.createElement(
              "span"
            );

          particle.className =
            "tef-reward-popup__confetti-piece";

          particle.style.setProperty(
            "--tef-confetti-x",
            `${x}px`
          );

          particle.style.setProperty(
            "--tef-confetti-y",
            `${y}px`
          );

          particle.style.setProperty(
            "--tef-confetti-r",
            `${rotation}deg`
          );

          particle.style.setProperty(
            "--tef-confetti-size",
            `${size}px`
          );

          particle.style.setProperty(
            "--tef-confetti-delay",
            `${delay}ms`
          );

          particle.style.setProperty(
            "--tef-confetti-color-index",
            `${i % 5}`
          );

          confetti.appendChild(
            particle
          );
        }

        iconWrap.prepend(
          confetti
        );

        setTimeout(() => {
          confetti.remove();
        }, 1500);
      }
    }

    node.hidden = false;

    requestAnimationFrame(() => {
      positionNearTrigger();

      requestAnimationFrame(() => {
        node.classList.add(
          "is-visible"
        );
      });
    });

    hideTimer =
      setTimeout(
        () =>
          hidePopup(),
        CONFIG.autoHideMs
      );
  };

  const hidePopup = () => {
    const node = popup();

    if (!node) {
      return;
    }

    node.classList.remove(
      "is-visible"
    );

    setTimeout(() => {
      if (
        !node.classList.contains(
          "is-visible"
        )
      ) {
        node.hidden = true;
      }
    }, 250);
  };

  /*
   * ---------------------------------------------------------
   * TIER DETECTION
   * ---------------------------------------------------------
   */
  const crossedTiers = (
    oldTotal,
    newTotal
  ) => {
    const oldTier =
      getTier(oldTotal);

    const newTier =
      getTier(newTotal);

    const crossed = [];

    for (
      let i = oldTier + 1;
      i <= newTier;
      i++
    ) {
      if (CONFIG.tiers[i]) {
        crossed.push(
          CONFIG.tiers[i]
        );
      }
    }

    return crossed;
  };

  /*
   * ---------------------------------------------------------
   * CART STATE PROCESSING
   * ---------------------------------------------------------
   */
  const processCartState =
    async () => {
      if (!initialized) {
        return;
      }

      let cart;

      try {
        cart =
          await getCart();
      } catch {
        return;
      }

      const newTotal =
        cart.total_price /
        100;

      if (
        lastCartTotal === null
      ) {
        lastCartTotal =
          cart.total_price;

        return;
      }

      const oldTotal =
        lastCartTotal / 100;

      /*
       * Immediately update the baseline.
       */
      lastCartTotal =
        cart.total_price;

      /*
       * Removing or decreasing products
       * never produces a popup.
       */
      if (
        newTotal <= oldTotal
      ) {
        return;
      }

      /*
       * -----------------------------------------------------
       * ACTUAL TIER UNLOCK
       * -----------------------------------------------------
       */
      const crossed =
        crossedTiers(
          oldTotal,
          newTotal
        );

      if (crossed.length) {
        const last =
          crossed[
            crossed.length - 1
          ];

        /*
         * One tier unlocked.
         */
        if (
          crossed.length === 1
        ) {
          showPopup({
            eyebrow:
              "✦ Mystery Gift Unlocked ✦",

            title:
              last.label,

            message:
              "You've unlocked a mystery gift!",

            stars:
              last.stars,

            celebration:
              true,

            tierIndex:
              CONFIG.tiers.indexOf(last)
          });
        }

        /*
         * Multiple tiers unlocked
         * with one Add to Cart.
         */
        else {
          showPopup({
            eyebrow:
              "✦ Double Unlock! ✦",

            title:
              `${crossed.length} Mystery Gifts Unlocked`,

            message:
              `You just unlocked ${crossed
                .map(
                  t => t.label
                )
                .join(
                  " and "
                )}!`,

            stars:
              "✦✦✦",

            celebration:
              true,

            tierIndex:
              CONFIG.tiers.indexOf(last)
          });
        }

        return;
      }

      /*
       * -----------------------------------------------------
       * FIRST PROGRESS MESSAGE
       * -----------------------------------------------------
       *
       * This is NOT a real tier.
       *
       * It only appears on the first successful Add to Cart
       * when the customer is still below Tier 1.
       */
      if (
        CONFIG.showFirstProgressMessage &&
        oldTotal === 0 &&
        newTotal > 0 &&
        CONFIG.tiers.length &&
        newTotal <
          CONFIG.tiers[0].threshold
      ) {
        const remaining =
          CONFIG.tiers[0].threshold -
          newTotal;

        showPopup({
          eyebrow:
            "Mystery Gifts",

          title:
            `${money(remaining)} away`,

          /*
           * NEW wording:
           *
           * "Add [calculated amount] more to unlock
           *  a Mystery Gift"
           */
          message:
            `Add ${money(
              remaining
            )} more to unlock a Mystery Gift`,

          /*
           * No lower stars.
           */
          stars:
            "",

          /*
           * No celebration stars above
           * the cactus either.
           */
          celebration:
            false
        });
      }
    };

  /*
   * ---------------------------------------------------------
   * CART CHECK QUEUE
   * ---------------------------------------------------------
   */
  const queueCartCheck = () => {
    cartCheckQueue =
      cartCheckQueue
        .then(
          async () => {
            await new Promise(
              resolve =>
                setTimeout(
                  resolve,
                  CONFIG.cartSettleMs
                )
            );

            await processCartState();
          }
        )
        .catch(
          () => {}
        );

    return cartCheckQueue;
  };

  /*
   * ---------------------------------------------------------
   * ADD TO CART BUTTON MEMORY
   * ---------------------------------------------------------
   */
  const rememberTrigger = (
    event
  ) => {
    const target =
      event.target;

    if (
      !(target instanceof Element)
    ) {
      return;
    }

    const button =
      target.closest(
        'button[name="add"], button[type="submit"], input[type="submit"], [data-add-to-cart], [name="add"]'
      );

    if (!button) {
      return;
    }

    const form =
      button.closest("form");

    const action =
      form?.getAttribute(
        "action"
      ) || "";

    if (
      form &&
      !action.includes(
        "/cart/add"
      ) &&
      !button.matches(
        '[data-add-to-cart], [name="add"]'
      )
    ) {
      return;
    }

    lastTrigger = button;
  };

  /*
   * ---------------------------------------------------------
   * FETCH WATCHER
   * ---------------------------------------------------------
   */
  const installFetchWatcher =
    () => {
      const originalFetch =
        window.fetch.bind(
          window
        );

      window.fetch =
        async (...args) => {
          const isAdd =
            isCartAddRequest(
              args[0],
              args[1]
            );

          const isChange =
            isCartChangeRequest(
              args[0],
              args[1]
            );

          const response =
            await originalFetch(
              ...args
            );

          /*
           * Successful Add to Cart.
           */
          if (
            isAdd &&
            response.ok
          ) {
            queueCartCheck();
          }

          /*
           * Successful cart change.
           *
           * SILENT baseline update.
           * Never triggers a popup.
           */
          if (
            isChange &&
            response.ok
          ) {
            try {
              const data =
                await response
                  .clone()
                  .json();

              if (
                typeof data.total_price ===
                "number"
              ) {
                lastCartTotal =
                  data.total_price;
              }
            } catch {
              /*
               * Fallback if the /change response
               * doesn't contain JSON cart data.
               */
              try {
                const cart =
                  await getCart();

                lastCartTotal =
                  cart.total_price;
              } catch {
                /* Ignore sync failure. */
              }
            }
          }

          return response;
        };
    };

  /*
   * ---------------------------------------------------------
   * XHR WATCHER
   * ---------------------------------------------------------
   */
  const installXhrWatcher =
    () => {
      const originalOpen =
        XMLHttpRequest
          .prototype
          .open;

      const originalSend =
        XMLHttpRequest
          .prototype
          .send;

      XMLHttpRequest
        .prototype
        .open =
        function (
          method,
          url,
          ...rest
        ) {
          const upperMethod =
            String(
              method
            ).toUpperCase();

          this.__tefCartAdd =
            upperMethod ===
              "POST" &&
            isCartAddUrl(url);

          this.__tefCartChange =
            upperMethod ===
              "POST" &&
            isCartChangeUrl(url);

          return originalOpen.call(
            this,
            method,
            url,
            ...rest
          );
        };

      XMLHttpRequest
        .prototype
        .send =
        function (
          ...args
        ) {
          /*
           * Successful Add to Cart.
           */
          if (
            this.__tefCartAdd
          ) {
            this.addEventListener(
              "load",
              () => {
                if (
                  this.status >=
                    200 &&
                  this.status <
                    300
                ) {
                  queueCartCheck();
                }
              },
              {
                once: true
              }
            );
          }

          /*
           * Successful cart change.
           *
           * SILENT baseline update.
           */
          if (
            this.__tefCartChange
          ) {
            this.addEventListener(
              "load",
              () => {
                if (
                  this.status >=
                    200 &&
                  this.status <
                    300
                ) {
                  let updatedTotal =
                    null;

                  try {
                    const data =
                      JSON.parse(
                        this.responseText
                      );

                    if (
                      typeof data.total_price ===
                      "number"
                    ) {
                      updatedTotal =
                        data.total_price;
                    }
                  } catch {
                    /*
                     * Fall through to quiet
                     * /cart.js synchronization.
                     */
                  }

                  if (
                    updatedTotal !==
                    null
                  ) {
                    lastCartTotal =
                      updatedTotal;
                  } else {
                    getCart()
                      .then(
                        cart => {
                          lastCartTotal =
                            cart.total_price;
                        }
                      )
                      .catch(
                        () => {}
                      );
                  }
                }
              },
              {
                once: true
              }
            );
          }

          return originalSend.apply(
            this,
            args
          );
        };
    };

  /*
   * ---------------------------------------------------------
   * INITIALIZATION
   * ---------------------------------------------------------
   */
  const init = async () => {
    const node = popup();

    if (!node) {
      return;
    }

    /*
     * Install watchers before initial cart read.
     */
    installFetchWatcher();
    installXhrWatcher();

    try {
      const cart =
        await getCart();

      lastCartTotal =
        cart.total_price;

      initialized = true;
    } catch {
      initialized = true;
      lastCartTotal = 0;
    }

    document.addEventListener(
      "click",
      rememberTrigger,
      true
    );

    node
      .querySelector(
        "[data-popup-continue]"
      )
      ?.addEventListener(
        "click",
        (event) => {
          event.preventDefault();
          event.stopPropagation();

          hidePopup();
        }
      );

    node
      .querySelector(
        ".tef-reward-popup__close"
      )
      ?.addEventListener(
        "click",
        hidePopup
      );

    window.addEventListener(
      "resize",
      () => {
        if (!node.hidden) {
          positionNearTrigger();
        }
      }
    );

    window.addEventListener(
      "scroll",
      () => {
        if (!node.hidden) {
          positionNearTrigger();
        }
      },
      {
        passive: true
      }
    );
  };

  if (
    document.readyState ===
    "loading"
  ) {
    document.addEventListener(
      "DOMContentLoaded",
      init,
      {
        once: true
      }
    );
  } else {
    init();
  }
})();